import {
	Box,
	Button,
	CardActions,
	CardContent,
	FormHelperText,
	Grid,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { useNavigate, useParams } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import AppRichTextEditor from '@/common/components/editor/AppRichTextEditor';
import {
	useCreateChapterMutation,
	useUpdateChapterMutation,
} from '@/api/information/informationApiSlice';
import {
	CreateChapterRequest,
	Information330Chapter,
} from '@/api/DTO/information/information.interface';
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import VisuallyHiddenInput from '@/common/components/UploadInput';
import { FileInfo } from '@/api/DTO/common.interface';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { useFileUploader } from '@/hooks/useFileUploader';

interface Props {
	type: FormType;
	details?: Information330Chapter;
	setType: Dispatch<SetStateAction<FormType>>;
}

export default function ChapterInfoForm({ type, setType, details }: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [needConfirm, setNeedConfirm] = useState(false);
	const [currentTab, setCurrentTab] = useState('zh');
	const [createChapter] = useCreateChapterMutation();
	const [updateChapter] = useUpdateChapterMutation();
	const [coverImageObj, setCoverImageObj] = useState<FileInfo | null>(null);
	const { uploadFile } = useFileUploader();
	const { informationId } = useParams();
	const imageInputRef = useRef<HTMLInputElement>(null);

	const defaultValues: DefaultValues<CreateChapterRequest> = {
		titleEn: details?.titleEn || '',
		titleZh: details?.titleZh || '',
		contentEn: details?.contentEn || '',
		contentZh: details?.contentZh || '',
		overviewId: informationId,
	};

	const formSchema = Yup.object().shape({
		titleZh: Yup.string().required(t('errorMessage.required')),
		// contentZh: Yup.string().required(t('errorMessage.required')),
		thumbnail: Yup.string().required(t('errorMessage.pleaseSelect')),
	});
	const methods = useForm<CreateChapterRequest>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	useEffect(() => {
		const subscription = methods.watch((value) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	useEffect(() => {
		if (details) {
			if (details.coverImage) {
				setCoverImageObj(details.coverImage);
				methods.setValue('thumbnail', details.coverImage?.url);
			}
		}
	}, [details]);

	const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFile = event.target.files?.[0];
		if (!selectedFile) return;
		const res = await uploadFile(selectedFile);
		if (res) {
			setCoverImageObj(res);
			methods.setValue('thumbnail', res?.url);
			methods.clearErrors(`thumbnail`);
		}
	};

	const handleDeleteImage = () => {
		setCoverImageObj(null);
		methods.setValue('thumbnail', '');
		imageInputRef.current && (imageInputRef.current.value = '');
	};

	const onSubmit: SubmitHandler<CreateChapterRequest> = async (data) => {
		const obj = { ...data, coverImage: coverImageObj };
		const response =
			type == FormType.create
				? await createChapter(obj)
				: await updateChapter({
						...obj,
						id: details?.id || '',
						sort: details?.sort,
				  });

		if ('data' in response) {
			enqueueSnackbar(
				type == FormType.create
					? t('snackbar.createChaptertionSuccess')
					: t('snackbar.editChapterSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			if (type == FormType.create) {
				navigate(`/information-manage/information-list/info/${informationId}`);
			} else {
				setType(FormType.view);
			}
		}
	};

	const handleClose = () => {
		if (type == FormType.edit) return setType(FormType.view);
		navigate(`/information-manage/information-list/info/${informationId}`);
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Tabs value={currentTab} onChange={handleTabChange}>
							<Tab
								label={
									<Typography variant='body1'>{t('information.zh')}</Typography>
								}
								value='zh'
							/>
							<Tab
								label={
									<Typography variant='body1'>{t('information.en')}</Typography>
								}
								value='en'
							/>
						</Tabs>

						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							{currentTab == 'zh' && (
								<>
									<Grid item xs={12}>
										<AppTextField
											control={methods.control}
											name='titleZh'
											label={t('information.chapterTitle')}
											maxLength={20}
										/>
									</Grid>
									<Grid item xs={12}>
										<Typography variant='body2' color='common.neutral40'>
											{t('information.chapterText')}
										</Typography>
										<AppRichTextEditor
											control={methods.control}
											name='contentZh'
										/>
									</Grid>
								</>
							)}
							{currentTab == 'en' && (
								<>
									<Grid item xs={12}>
										<AppTextField
											control={methods.control}
											name='titleEn'
											label={t('information.chapterTitle_')}
											maxLength={100}
										/>
									</Grid>
									<Grid item xs={12}>
										<Typography variant='body2' color='common.neutral40'>
											{t('information.chapterText_')}
										</Typography>
										<AppRichTextEditor
											control={methods.control}
											name='contentEn'
										/>
									</Grid>
								</>
							)}
							<Grid item xs={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('information.chapterImage')}
								</Typography>
								<Box sx={{ margin: '10px 0' }}>
									{coverImageObj?.url && (
										<img
											src={coverImageObj?.url}
											width={110}
											height={132}
											style={{
												objectFit: 'contain',
											}}
										/>
									)}
								</Box>
								{coverImageObj?.url ? (
									<Button
										component='label'
										sx={{ height: 24 }}
										endIcon={<LoopOutlinedIcon />}
									>
										{t('information.changeImage')}
										<VisuallyHiddenInput
											type='file'
											name='thumbnail'
											accept='image/*'
											ref={imageInputRef}
											onChange={handleUpload}
										/>
									</Button>
								) : (
									<Button
										component='label'
										sx={{ height: 24 }}
										endIcon={<DriveFolderUploadOutlinedIcon />}
									>
										{t('information.uploadImage')}
										<VisuallyHiddenInput
											type='file'
											name='thumbnail'
											accept='image/*'
											ref={imageInputRef}
											onChange={handleUpload}
										/>
									</Button>
								)}
								{coverImageObj && (
									<Button
										component='label'
										sx={{ height: 24, marginLeft: '24px' }}
										color='error'
										endIcon={<DeleteOutlinedIcon />}
										onClick={handleDeleteImage}
									>
										{t('information.deleteImage')}
									</Button>
								)}

								<FormHelperText style={{ color: '#d32f2f' }}>
									{typeof methods.formState.errors.thumbnail?.message ===
									'string'
										? methods.formState.errors.thumbnail.message
										: ''}
								</FormHelperText>
							</Grid>
						</Grid>
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							onClick={() => {
								console.log(methods.formState.errors.thumbnail?.message);
							}}
							type='submit'
							variant='contained'
							disabled={!needConfirm && type === FormType.create}
						>
							{type === FormType.edit ? t('button.save') : t('button.create')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
